import { useRef, useLayoutEffect, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fullPagerConfig, computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@/utils/property';
import { Button, Card, Table, Space, Dropdown, Modal, Form, Input, Row, Col, Select, message, Tooltip, theme, Divider } from 'antd';
import { get, put } from '@/core/request';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import './StaffList.less';

const FILTER_FIELD_COUNT = 4;

export default function EnterpriseStaffList() {
    const { token } = theme.useToken();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const [filterForm] = Form.useForm();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const buttonColLayout = computeButtonColLayout(FILTER_FIELD_COUNT);
    const formLayout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 17 },
    };
    const [tableData, setTableData] = useState({});
    const [filterObj, setFilterObj] = useState({});
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    const handleFilterFinish = value => {
        setFilterObj(value);
        setCurrPage(1);
    };

    const handleFilterReset = () => {
        filterForm.resetFields();
        setFilterObj({});
        setCurrPage(1);
    };

    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const { name, phone, email, status } = filterObj;

    const getData = useCallback(() => {
        get(`/enterprise/staffs/query_page`, {
            limit: pageSize,
            page: currPage,
            name,
            phone,
            email,
            status,
        }).then(res => {
            if (res.status_code === 200) {
                const { list } = res.data;
                list.forEach(item => {
                    item.key = item.phone;
                });
                setTableData(res.data);
            }
        });
    }, [pageSize, currPage, name, phone, email, status]);

    useEffect(() => {
        getData();
    }, [getData]);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    const handleClickEdit = (tip, row) => {
        return () => {
            navigate('/enterprise/staff/new', { state: { tip, id: row?.id, name: row?.name } });
        };
    };

    const stopConfirm = row => {
        return () => {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: <span style={{ color: '#ff0000' }}>{row.status === 0 ? '冻结账号后，该员工将无法登录系统，确定冻结？' : '确认启用账号？'}</span>,
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    let status = 1;
                    if (row.status === 1) {
                        status = 0;
                    }
                    put('/enterprise/staffs/update', {
                        id: row.id,
                        status,
                    }).then(res => {
                        if (res.status_code === 200) {
                            if (row.status === 0) {
                                message.success('已冻结账号！');
                            } else {
                                message.success('启用账号成功！');
                            }
                            getData();
                        }
                    });
                },
            });
        };
    };

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [pedding, setPedding] = useState(false);
    const [passwordModal, setPasswordModal] = useState({
        staffName: '',
        password: '',
        id: 0,
    });

    const resetPassword = row => {
        return () => {
            form.resetFields();
            setPasswordModal({
                staffName: row.name,
                id: row.id,
            });
            setIsModalVisible(true);
        };
    };

    const handleOk = () => {
        if (pedding) return;
        setPedding(true);
        form.validateFields()
            .then(values => {
                put('/enterprise/staffs/update', {
                    id: passwordModal.id,
                    password: values.password,
                }).then(res => {
                    if (res.status_code === 200) {
                        message.success('修改密码成功！');
                        form.resetFields();
                        setIsModalVisible(false);
                    }
                });
            })
            .catch(_err => {});
        setPedding(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleRefresh = useCallback(() => {
        message.info('正在刷新', 1);
        changeCurrPage(1);
    }, [changeCurrPage]);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const columns = [
        { title: '员工ID', dataIndex: 'id' },
        { title: '姓名', dataIndex: 'name' },
        { title: '手机', dataIndex: 'phone' },
        { title: '企业邮箱', dataIndex: 'email' },
        { title: '工号', dataIndex: 'number' },
        {
            title: '部门',
            dataIndex: 'departments',
            render: operates =>
                operates.length > 0
                && operates
                    .map(item => item?.name)
                    .filter(Boolean)
                    .join('/'),
        },
        { title: '职务', dataIndex: 'post' },
        {
            title: '角色',
            dataIndex: 'roles',
            width: 300,
            render: operates => {
                const arr = operates.length > 0 && operates.map(item => item?.name).filter(Boolean);
                if (arr) return <span>{arr.join('/')}</span>;
            },
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: operates => <span className={operates === 1 ? 'close-status' : 'open-status'}>{operates === 1 ? '冻结' : '正常'}</span>,
        },
        {
            title: '操作',
            dataIndex: 'operate',
            width: 100,
            fixed: 'right',
            render: (_operates, row) => {
                const items = [
                    {
                        label: '重置密码',
                        key: 'reset',
                        onClick: resetPassword(row),
                    },
                    {
                        label: row.status === 1 ? '启用账号' : '冻结账号',
                        key: 'toggleStatus',
                        onClick: stopConfirm(row),
                        ...(row.status === 0 ? { danger: true } : {}),
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={handleClickEdit('edit', row)}>
                            编辑
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" aria-hidden="true" />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="page-container list-page-container enterprise-staff-page">
            <PageHeader title="员工管理" breadcrumb={[]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="enterprise-staff-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} form={filterForm} onFinish={handleFilterFinish} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="姓名" name="name" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="员工姓名" autoComplete="off" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="手机号" name="phone" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="员工手机号" autoComplete="off" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="邮箱地址" name="email" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="员工邮箱地址" autoComplete="off" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="状态" name="status" style={filterFormItemStyle}>
                                    <Select allowClear placeholder="选择账号状态">
                                        <Select.Option value={0}>正常</Select.Option>
                                        <Select.Option value={1}>冻结</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                            检索
                                        </Button>
                                        <Button type="default" onClick={handleFilterReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container enterprise-staff-table-region">
                <div className="main-toolbar enterprise-staff-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleClickEdit('add')}>
                                新增员工
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap enterprise-staff-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData.list}
                        loading={false}
                        bordered
                        sticky
                        size="medium"
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={paginationProps}
                    />
                </div>
            </div>

            {isModalVisible && (
                <Modal title="重置密码" open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
                    <Form form={form} {...formLayout}>
                        <Form.Item label="员工账号">{passwordModal.staffName}</Form.Item>

                        <Form.Item
                            name="password"
                            label="新密码"
                            required={true}
                            help="密码不少于6位，必须包含数字或字母（大写或小写）"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*]{6,}$/.test(value)) {
                                            return Promise.reject('密码不少于6位，必须包含数字或字母（大写或小写）');
                                        } else {
                                            return Promise.resolve();
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input placeholder="为该员工设置新的密码" allowClear autoComplete="off" />
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </div>
    );
}
